/**
 * @file poStatus.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-15
 * @modified 2026-09-15
 * @description One purchase order status vocabulary, and how it reads.
 *
 *              Synced POs carry Xero's statuses, uppercase: DRAFT, AUTHORISED,
 *              BILLED, DELETED. Local POs used to be written in a title-case
 *              vocabulary of their own (Draft, Sent, Confirmed, Deleted), so a
 *              filter for one missed the other, and the bill-linking code — which
 *              marks a local PO 'BILLED' and reverts it to 'AUTHORISED' — was
 *              already speaking Xero's language. Local POs now store the same
 *              uppercase values; SENT and CONFIRMED are the two local-only steps.
 *
 *              DELETED reads as "Archived". Nothing is removed — an archived PO
 *              is kept, just out of the active lists — and the stored value
 *              stays DELETED so every existing exclusion (analytics, the rep
 *              dashboard, bill linking) keeps working without being taught a
 *              new word.
 */

export const PO_STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'AUTHORISED', label: 'Authorised' },
  { value: 'SENT', label: 'Sent' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'BILLED', label: 'Billed' },
  { value: 'DELETED', label: 'Archived' },
];

/** A new local PO is raised to be sent, not parked. */
export const DEFAULT_PO_STATUS = 'AUTHORISED';

export const normalisePoStatus = (status) => String(status || '').trim().toUpperCase();

/** How a status reads, whichever case it was stored in. */
export function poStatusLabel(status) {
  const value = normalisePoStatus(status);
  const option = PO_STATUS_OPTIONS.find(o => o.value === value);
  if (option) return option.label;
  return value ? value.charAt(0) + value.slice(1).toLowerCase() : '';
}

/** Archived (or voided) — out of the active lists, not sent, not billed. */
export const isArchivedPoStatus = (status) => ['DELETED', 'VOIDED'].includes(normalisePoStatus(status));

/** The badge class for a status, matching the shared .status-badge variants. */
export function poStatusBadgeClass(status) {
  const map = {
    DRAFT: 'draft',
    AUTHORISED: 'authorised',
    SENT: 'sent',
    CONFIRMED: 'confirmed',
    BILLED: 'billed',
    DELETED: 'deleted',
  };
  return `status-badge ${map[normalisePoStatus(status)] || 'default'}`;
}
